/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useLogProfileForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing log profile form state and input changes.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo } from 'react';
import { LogProfileFormData, LogPermission } from '../types';

export function useLogProfileForm() {
  const [formData, setFormData] = useState<LogProfileFormData>({
    name: '',
    description: '',
    permissions: [],
  });

  const [originalData, setOriginalData] = useState<LogProfileFormData>({
    name: '',
    description: '',
    permissions: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const hasChanges = useMemo(() => {
    // Compare basic fields
    if (formData.name !== originalData.name || formData.description !== originalData.description) {
      return true;
    }

    // Compare permissions (deep comparison)
    const currentPerms = JSON.stringify([...formData.permissions].sort((a, b) => a.moduleSlug.localeCompare(b.moduleSlug)));
    const originalPerms = JSON.stringify([...originalData.permissions].sort((a, b) => a.moduleSlug.localeCompare(b.moduleSlug)));

    return currentPerms !== originalPerms;
  }, [formData, originalData]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: [],
    });
    setErrors({});
  };

  return {
    formData,
    setFormData,
    originalData,
    setOriginalData,
    errors,
    setErrors,
    handleInputChange,
    hasChanges,
    resetForm,
  };
}

